import { get, set } from 'idb-keyval';
import { loadChronik, saveChronikEntry, batchSaveChronikEntries } from './services/serverPackageService';
import { aktueller as aktuellerMitarbeiter } from './services/mitarbeiter';

/**
 * 🔄 CHRONIK HELPER - Zentrale Logging-Funktionen (Server-Version)
 * 
 * Alle Firebase-Aufrufe entfernt. Chronik wird über den Server synchronisiert.
 * Lokaler IndexedDB-Cache bleibt für Offline-/Performance-Zwecke.
 */

// 🔑 SYNC-AKTIONEN: Nur diese werden zum Server synchronisiert
const SYNC_ACTIONS = [
  'paket_hinzugefuegt',
  'paket_geaendert',
  'paket_ausgegeben',
  'paket_geloescht',
  'paket_zurueckgeschickt',
  'cleanup'
];

// Hilfsfunktion: Generiere eindeutige ID
const generiereId = () => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Hilfsfunktion: Bereinige alte Einträge (> 10 Tage) und limitiere auf 300
const bereinigenChronik = (chronik) => {
  const jetzt = Date.now();
  const zehnTageMs = 10 * 24 * 60 * 60 * 1000;
  
  let bereinigte = chronik.filter(eintrag => {
    return (jetzt - eintrag.timestamp) < zehnTageMs;
  });

  // Begrenze Suchen auf max 20
  const suchen = bereinigte.filter(e => e.action === 'suche' || e.action === 'tablet_suche');
  const anderePakete = bereinigte.filter(e => e.action !== 'suche' && e.action !== 'tablet_suche');
  
  let begrenzteSuchen = suchen;
  if (suchen.length > 20) {
    begrenzteSuchen = suchen
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 20);
  }
  
  bereinigte = [...anderePakete, ...begrenzteSuchen];
  
  if (bereinigte.length > 300) {
    bereinigte = bereinigte
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 300);
  }

  return bereinigte;
};

// Hilfsfunktion: Prüfe ob Duplikat
const findeDuplikat = (chronik, neuerEintrag) => {
  const fuenfMinutenMs = 5 * 60 * 1000;
  
  return chronik.findIndex(eintrag => {
    if (Math.abs(eintrag.timestamp - neuerEintrag.timestamp) > fuenfMinutenMs) return false;
    if (eintrag.action !== neuerEintrag.action) return false;
    
    if (neuerEintrag.action === 'suche' || neuerEintrag.action === 'tablet_suche') {
      return eintrag.suchQuery === neuerEintrag.suchQuery;
    }
    
    return eintrag.trackingId === neuerEintrag.trackingId;
  });
};

// ============================================================
// Mapping: Client-Format ↔ Server-Format
// ============================================================

/**
 * Server-Chronik-Eintrag → Client-Format
 */
function mapChronikFromServer(serverEntry) {
  const details = serverEntry.details || {};
  return {
    id: serverEntry.firebase_id,
    action: serverEntry.aktion,
    paketId: serverEntry.paket_id || undefined,
    timestamp: new Date(serverEntry.created_at).getTime(),
    synced: true,
    ...details
  };
}

/**
 * Client-Chronik-Eintrag → Server-Format für PUT/batch
 */
function mapChronikToServer(entry) {
  const { id, action, paketId, timestamp, synced, wiederholungen, ...details } = entry;
  return {
    id,
    aktion: action,
    paketId: paketId || null,
    details,
    timestamp
  };
}

// ============================================================
// Core-Funktionen
// ============================================================

/**
 * Füge Chronik-Eintrag hinzu (lokal + Server)
 */
export const addChronikEintrag = async (eintrag) => {
  try {
    if (!eintrag.action) {
      console.warn("Chronik: Keine Action angegeben");
      return;
    }

    // Erstelle vollständigen Eintrag
    const vollstaendigerEintrag = {
      id: generiereId(),
      timestamp: Date.now(),
      action: eintrag.action,
      wiederholungen: 0
    };
    
    // Nur definierte Felder hinzufügen
    if (eintrag.paketId) vollstaendigerEintrag.paketId = eintrag.paketId;
    if (eintrag.paketName) vollstaendigerEintrag.paketName = eintrag.paketName;
    if (eintrag.trackingId) vollstaendigerEintrag.trackingId = eintrag.trackingId;
    if (eintrag.suchQuery) vollstaendigerEintrag.suchQuery = eintrag.suchQuery;
    if (eintrag.aenderungen) vollstaendigerEintrag.aenderungen = eintrag.aenderungen;
    if (eintrag.deletedIds) vollstaendigerEintrag.deletedIds = eintrag.deletedIds;
    if (eintrag.deletedCount) vollstaendigerEintrag.deletedCount = eintrag.deletedCount;
    if (eintrag.resultingHash) vollstaendigerEintrag.resultingHash = eintrag.resultingHash;
    // signatureDataUrl bewusst NICHT in die Chronik (Bild bleibt am Paket, Review 21.10.2026)
    if (eintrag.abgeholtVonName) vollstaendigerEintrag.abgeholtVonName = eintrag.abgeholtVonName;
    if (eintrag.abholUnterschriftAm) vollstaendigerEintrag.abholUnterschriftAm = eintrag.abholUnterschriftAm;
    if (eintrag.regalplatz) vollstaendigerEintrag.regalplatz = eintrag.regalplatz;
    if (eintrag.quelle) vollstaendigerEintrag.quelle = eintrag.quelle;

    // 👷 Aktuell angemeldeten Mitarbeiter automatisch im Chronik-Eintrag festhalten
    const wer = aktuellerMitarbeiter();
    const mitarbeiterId = eintrag.mitarbeiterId || wer?.id || '';
    const mitarbeiterName = eintrag.mitarbeiterName || wer?.name || '';
    if (mitarbeiterId) vollstaendigerEintrag.mitarbeiterId = mitarbeiterId;
    if (mitarbeiterName) vollstaendigerEintrag.mitarbeiterName = mitarbeiterName;

    // Lade lokale Chronik
    let chronik = await get('chronik') || [];
    
    // Prüfe auf Duplikat
    const duplikatIndex = findeDuplikat(chronik, vollstaendigerEintrag);
    
    if (duplikatIndex !== -1) {
      chronik[duplikatIndex].timestamp = vollstaendigerEintrag.timestamp;
      chronik[duplikatIndex].wiederholungen = (chronik[duplikatIndex].wiederholungen || 0) + 1;
    } else {
      chronik.unshift(vollstaendigerEintrag);
    }

    // Bereinigen + lokal speichern
    chronik = bereinigenChronik(chronik);
    await set('chronik', chronik);

    // Nur Paket-Aktionen zum Server synchronisieren (Suchen bleiben lokal)
    const sollSyncen = SYNC_ACTIONS.includes(eintrag.action);
    
    if (!sollSyncen) {
      return chronik;
    }
    
    if (!vollstaendigerEintrag.paketId && eintrag.action !== 'cleanup') {
      console.warn("📋 Chronik: Sync-Aktion ohne paketId:", eintrag.action);
      return chronik;
    }

    // Zum Server senden (fire-and-forget)
    try {
      const serverData = mapChronikToServer(vollstaendigerEintrag);
      await saveChronikEntry(vollstaendigerEintrag.id, serverData);
      console.log("📋 Chronik → Server:", eintrag.action,
        eintrag.action === 'cleanup' ? `deletedCount: ${vollstaendigerEintrag.deletedCount}` : `paketId: ${vollstaendigerEintrag.paketId}`);
    } catch (serverError) {
      // Nicht kritisch - bleibt lokal, wird beim nächsten Sync hochgeladen
      console.warn("📋 Chronik: Server-Sync fehlgeschlagen (nicht kritisch):", serverError.message);
      vollstaendigerEintrag.synced = false;
      await set('chronik', chronik);
    }

    return chronik;

  } catch (error) {
    console.error("Fehler beim Hinzufügen des Chronik-Eintrags:", error);
  }
};

/**
 * 🔄 SYNC CHRONIK: Lade vom Server beim Login
 * Cache: Lädt nur wenn letzte Sync > 1 Stunde her
 */
export const syncChronikAfterLogin = async (forceRefresh = false) => {
  try {
    const CACHE_KEY = 'chronik_last_sync';
    const CACHE_DURATION = 60 * 60 * 1000; // 1 Stunde
    
    const lastSync = await get(CACHE_KEY);
    const now = Date.now();
    
    if (!forceRefresh && lastSync && (now - lastSync) < CACHE_DURATION) {
      const lokaleChronik = await get('chronik') || [];
      console.log("📋 Chronik: Cache gültig, überspringe Server-Sync. Lokal:", lokaleChronik.length);
      return bereinigenChronik(lokaleChronik);
    }

    // Lade lokale Chronik
    const lokaleChronik = await get('chronik') || [];
    
    // Lade vom Server
    let serverChronik = [];
    try {
      const response = await loadChronik({ limit: 300 });
      const rawEntries = response?.chronik || [];
      serverChronik = rawEntries.map(mapChronikFromServer);
      console.log("📋 Chronik: Vom Server geladen:", serverChronik.length, "Einträge");
    } catch (err) {
      console.warn("📋 Chronik: Server nicht erreichbar, nutze lokale Daten:", err.message);
      await set(CACHE_KEY, Date.now());
      return bereinigenChronik(lokaleChronik);
    }

    // Merge: Server hat Priorität
    const chronikMap = new Map();
    serverChronik.forEach(e => { if (e.id) chronikMap.set(e.id, e); });
    lokaleChronik.forEach(e => { if (e.id && !chronikMap.has(e.id)) chronikMap.set(e.id, e); });

    const gemergteChronik = Array.from(chronikMap.values())
      .sort((a, b) => b.timestamp - a.timestamp);

    const bereinigte = bereinigenChronik(gemergteChronik);
    await set('chronik', bereinigte);
    await set(CACHE_KEY, Date.now());

    // Lokale ungesynchte Einträge zum Server pushen
    const ungeSyncht = lokaleChronik.filter(e => 
      e.synced === false && SYNC_ACTIONS.includes(e.action)
    );
    if (ungeSyncht.length > 0) {
      try {
        const serverEntries = ungeSyncht.map(mapChronikToServer);
        await batchSaveChronikEntries(serverEntries);
        console.log("📋 Chronik:", ungeSyncht.length, "lokale Einträge zum Server gepusht");
      } catch (pushErr) {
        console.warn("📋 Chronik: Push fehlgeschlagen:", pushErr.message);
      }
    }

    console.log("📋 Chronik: Sync abgeschlossen:", bereinigte.length, "Einträge");
    return bereinigte;

  } catch (error) {
    console.error("📋 Chronik: Sync fehlgeschlagen:", error);
    return await get('chronik') || [];
  }
};

/**
 * 📋 Lade Chronik aus lokalem IndexedDB
 */
export const ladeChronik = async () => {
  try {
    const chronik = await get('chronik') || [];
    return chronik;
  } catch (error) {
    console.error("Fehler beim Laden der Chronik:", error);
    return [];
  }
};

// ============================================================
// Log-Funktionen (unverändert — rufen intern addChronikEintrag auf)
// ============================================================

export const logPaketHinzugefuegt = async (paketId, empfaenger, absender, trackingNumber) => {
  return addChronikEintrag({
    action: 'paket_hinzugefuegt',
    paketId,
    paketName: empfaenger || 'Unbekannt',
    trackingId: trackingNumber || 'Keine'
  });
};

export const logPaketGeaendert = async (paketId, empfaenger, absender, trackingNumber) => {
  return addChronikEintrag({
    action: 'paket_geaendert',
    paketId,
    paketName: empfaenger || 'Unbekannt',
    trackingId: trackingNumber || 'Keine'
  });
};

export const logPaketAusgegeben = async (paketId, empfaenger, absender, trackingNumber, signatureData) => {
  return addChronikEintrag({
    action: 'paket_ausgegeben',
    paketId,
    paketName: empfaenger || 'Unbekannt',
    trackingId: trackingNumber || 'Keine',
    ...(signatureData && typeof signatureData === 'object' ? {
      ...(signatureData.abgeholtVonName ? { abgeholtVonName: signatureData.abgeholtVonName } : {}),
      ...(signatureData.abholUnterschriftAm ? { abholUnterschriftAm: signatureData.abholUnterschriftAm } : {}),
    } : {}),
  });
};

export const logPaketGeloescht = async (paketId, empfaenger, absender, trackingNumber) => {
  return addChronikEintrag({
    action: 'paket_geloescht',
    paketId,
    paketName: empfaenger || 'Unbekannt',
    trackingId: trackingNumber || 'Keine'
  });
};

export const logPaketZurueckgeschickt = async (paketId, empfaenger, absender, trackingNumber) => {
  return addChronikEintrag({
    action: 'paket_zurueckgeschickt',
    paketId,
    paketName: empfaenger || 'Unbekannt',
    trackingId: trackingNumber || 'Keine'
  });
};

export const logPaketBearbeitet = async (paketId, empfaenger, absender, trackingNumber, aenderungen) => {
  return addChronikEintrag({
    action: 'paket_geaendert',
    paketId,
    paketName: empfaenger || 'Unbekannt',
    trackingId: trackingNumber || 'Keine',
    aenderungen: aenderungen
  });
};

export const logCleanup = async (deletedIds, cleanedPackages) => {
  return addChronikEintrag({
    action: 'cleanup',
    deletedIds: deletedIds || [],
    deletedCount: (deletedIds || []).length,
    paketId: 'cleanup_' + Date.now()
  });
};

export const logSuche = async (suchbegriff, treffer) => {
  return addChronikEintrag({
    action: 'suche',
    suchQuery: suchbegriff,
    paketName: `${treffer} Treffer`
  });
};

export const logTabletSuche = async (suchbegriff, treffer, actions) => {
  return addChronikEintrag({
    action: 'tablet_suche',
    suchQuery: suchbegriff,
    paketName: `${treffer} Treffer`
  });
};

// ============================================================
// Hash-Funktionen (vereinfacht — Server ist autoritativ)
// ============================================================

/**
 * Berechne SHA-256 Hash über Chronik-Einträge (pure Funktion)
 */
export async function calculateChronikHash(chronikEintraege) {
  try {
    if (!chronikEintraege || chronikEintraege.length === 0) return 'empty';
    
    const syncRelevant = chronikEintraege
      .filter(e => SYNC_ACTIONS.includes(e.action))
      .sort((a, b) => a.timestamp - b.timestamp);
    
    if (syncRelevant.length === 0) return 'empty';
    
    const hashInput = syncRelevant.map(e => 
      `${e.id || ''}_${e.action}_${e.paketId || ''}_${e.timestamp}`
    ).join('|');
    
    const encoder = new TextEncoder();
    const data = encoder.encode(hashInput);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    console.error('📋 Chronik-Hash-Berechnung fehlgeschlagen:', error);
    return `fallback_${chronikEintraege.length}_${Date.now()}`;
  }
}

/**
 * Hash aktualisieren — No-Op (Server verwaltet intern)
 */
export async function updateChronikHash(chronikEintraege) {
  // Server ist autoritativ, kein Client-seitiges Hash-Update nötig
  console.log('📋 Chronik-Hash wird vom Server verwaltet');
}

// ============================================================
// Sync-Funktionen (Server-basiert)
// ============================================================

/**
 * 📥 Lade neue Chronik-Einträge vom Server (Delta-Sync)
 */
export async function syncNewChronikEntries(lastSync = 0) {
  try {
    const response = await loadChronik({ since: lastSync });
    const rawEntries = response?.chronik || [];
    const entries = rawEntries.map(mapChronikFromServer);
    console.log(`📋 ${entries.length} neue Chronik-Einträge vom Server geladen`);
    return entries;
  } catch (error) {
    console.error('📋 Fehler beim Laden neuer Chronik-Einträge:', error);
    return [];
  }
}

/**
 * 🔍 Prüft ob Chronik-Sync nötig ist (zeitbasiert)
 */
export async function checkChronikSyncNeeded() {
  try {
    const lastSync = await get('chronik_last_sync') || 0;
    const timeSinceLastSync = Date.now() - lastSync;
    // Sync nötig wenn > 5 Minuten seit letztem Sync
    return timeSinceLastSync > 5 * 60 * 1000;
  } catch (error) {
    console.error('📋 Fehler beim Chronik-Sync-Check:', error);
    return true;
  }
}

/**
 * 📋 LAZY LOADING: Chronik nur bei Bedarf laden (beim Öffnen des Tabs)
 */
export async function loadChronikOnDemand() {
  try {
    // 1. Lokale Chronik laden
    const lokaleChronik = await get('chronik') || [];
    
    // 2. Prüfe letzte Synchronisation
    const lastSync = await get('lastChronikSync') || 0;
    const timeSinceLastSync = Date.now() - lastSync;
    
    // Nur alle 60 Sekunden neu laden
    if (timeSinceLastSync < 60000 && lokaleChronik.length > 0) {
      console.log('📋 Chronik aus Cache (< 60s alt)');
      return lokaleChronik;
    }
    
    // 3. Neue Einträge vom Server laden
    let neueEintraege = [];
    try {
      neueEintraege = await syncNewChronikEntries(lastSync);
    } catch (err) {
      console.warn('📋 Server nicht erreichbar, nutze Cache');
      await set('lastChronikSync', Date.now());
      return lokaleChronik;
    }
    
    if (neueEintraege.length > 0) {
      const chronikMap = new Map();
      lokaleChronik.forEach(e => { if (e.id) chronikMap.set(e.id, e); });
      neueEintraege.forEach(e => { if (e.id) chronikMap.set(e.id, e); });
      
      const mergedChronik = Array.from(chronikMap.values())
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 300);
      
      await set('chronik', mergedChronik);
      await set('lastChronikSync', Date.now());
      return mergedChronik;
    }
    
    await set('lastChronikSync', Date.now());
    return lokaleChronik;
    
  } catch (error) {
    console.error('📋 Fehler beim Laden der Chronik:', error);
    return await get('chronik') || [];
  }
}

// ============================================================
// Delta-Sync Funktionen
// ============================================================

/**
 * Lade Chronik-Einträge seit einem bestimmten Timestamp
 */
export const loadChronikSeit = async (sinceTimestamp, limit = 50) => {
  try {
    const response = await loadChronik({ since: sinceTimestamp, limit });
    const rawEntries = response?.chronik || [];
    const entries = rawEntries.map(mapChronikFromServer);
    console.log(`📋 Delta-Sync: ${entries.length} Einträge seit ${new Date(sinceTimestamp).toLocaleString()}`);
    return entries;
  } catch (error) {
    console.error("📋 Delta-Sync: Fehler:", error);
    return [];
  }
};

/**
 * Prüfe ob Delta-Sync möglich ist
 */
export const kannDeltaSync = (eintraege) => {
  const paketAktionen = eintraege.filter(e => SYNC_ACTIONS.includes(e.action));
  return paketAktionen.every(e => e.paketId || e.action === 'cleanup');
};

/**
 * Extrahiere Paket-IDs aus Chronik-Einträgen für Delta-Sync
 */
export const extractPaketIdsFromChronik = (eintraege) => {
  const toLoad = [];
  const toDelete = [];
  
  for (const eintrag of eintraege) {
    if (eintrag.action === 'cleanup' && eintrag.deletedIds) {
      toDelete.push(...eintrag.deletedIds);
      continue;
    }
    
    if (!eintrag.paketId) continue;
    
    if (eintrag.action === 'paket_geloescht') {
      toDelete.push(eintrag.paketId);
    } else if (SYNC_ACTIONS.includes(eintrag.action)) {
      if (!toLoad.includes(eintrag.paketId) && !toDelete.includes(eintrag.paketId)) {
        toLoad.push(eintrag.paketId);
      }
    }
  }
  
  const toLoadFinal = toLoad.filter(id => !toDelete.includes(id));
  console.log(`📋 Delta-Sync: ${toLoadFinal.length} Pakete zu laden, ${toDelete.length} zu löschen`);
  return { toLoad: toLoadFinal, toDelete };
};

/**
 * 🔍 Suche nach Chronik-Einträgen seit einem Hash
 * Vereinfacht: Lädt die letzten N Einträge und sucht den Hash
 */
export const getChronikEntriesSinceHash = async (targetHash, limit = 100) => {
  try {
    const response = await loadChronik({ limit });
    const rawEntries = response?.chronik || [];
    const chronikEntries = rawEntries.map(mapChronikFromServer);
    
    const hashIndex = chronikEntries.findIndex(e => e.resultingHash === targetHash);
    
    if (hashIndex === -1) {
      console.log(`📋 Hash-Suche: Hash ${targetHash?.substring(0, 8)}... nicht gefunden`);
      return { found: false, entries: [] };
    }
    
    const entriesSinceHash = chronikEntries.slice(0, hashIndex);
    entriesSinceHash.sort((a, b) => a.timestamp - b.timestamp);
    
    console.log(`📋 Hash-Suche: ${entriesSinceHash.length} Einträge seit Hash`);
    return { found: true, entries: entriesSinceHash };
    
  } catch (error) {
    console.error("📋 Hash-Suche: Fehler:", error);
    return { found: false, entries: [] };
  }
};

// Alias für konsistente Namensgebung
export const loadChronikSinceTimestamp = loadChronikSeit;

// Export SYNC_ACTIONS für externe Nutzung
export { SYNC_ACTIONS };
